/**
 * 积分支付守卫 composable
 *
 * 在 PC 端使用，封装余额检查 + PayDialog 状态管理。
 * 配合 <PayDialog> 组件使用。
 *
 * @example
 * // 在页面 <script setup> 中
 * import { usePaymentGuard } from '@/composables/usePaymentGuard'
 * import PayDialog from '@/components/common/PayDialog.vue'
 *
 * const {
 *   showPayDialog,
 *   payDialogCost,
 *   payDialogBalance,
 *   payDialogFeature,
 *   checkPoints,
 *   goToPoints,
 * } = usePaymentGuard()
 *
 * async function handleTarotRead() {
 *   const canProceed = await checkFeatureCost('tarot', '塔罗解读')
 *   if (!canProceed) return
 *   // ... 执行 AI 解读
 * }
 *
 * // 模板中
 * // <PayDialog
 * //   v-model:visible="showPayDialog"
 * //   :cost="payDialogCost"
 * //   :balance="payDialogBalance"
 * //   :feature="payDialogFeature"
 * //   :rmb-price="payDialogRmbPrice"
 * //   @goPoints="goToPoints"
 * // />
 */
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { pointService } from '@/api/pointService'
import { API_CONFIG } from '@/config'

export function usePaymentGuard() {
  const router = useRouter()

  const showPayDialog = ref(false)
  const payDialogCost = ref(0)
  const payDialogBalance = ref(0)
  const payDialogFeature = ref('')
  const payDialogRmbPrice = ref(0)

  /**
   * 检查余额并决定是否弹出 PayDialog
   *
   * @param cost        - 需要的积分数量
   * @param featureName - 功能名称（用于弹窗展示）
   * @returns true 表示可以继续执行，false 表示已弹窗
   */
  async function checkPoints(cost, featureName) {
    try {
      const res = await pointService.getSummary()
      const balance = res?.balance ?? 0
      if (balance >= cost) return true

      payDialogCost.value = cost
      payDialogBalance.value = balance
      payDialogFeature.value = featureName
      showPayDialog.value = true
      return false
    } catch {
      // API 异常（未登录等），让调用方自行处理后续逻辑
      return true
    }
  }

  /**
   * 动态查询功能定价
   *
   * GET /api/membership/feature-cost?feature=X，返回平铺 { cost, base_cost, rmb_price, plan, free }。
   * 与 BaziPanel.vue 的 inline 实现同一模式：
   * 查价失败/非200/结构异常 → 返回 null，调用方跳过前端守卫，由后端扣费权威兜底（真不足会返回 429）。
   *
   * @param feature - 功能标识（'tarot' | 'iching' | 'dream' | 'bazi' ...）
   * @returns { cost, rmbPrice, free } 或 null
   */
  async function getFeatureCost(feature) {
    try {
      const token = localStorage.getItem('token')
      const res = await fetch(
        `${API_CONFIG.baseUrl}/api/membership/feature-cost?feature=${encodeURIComponent(feature)}`,
        { headers: token ? { 'Authorization': `Bearer ${token}` } : {} }
      )
      if (!res.ok) {
        console.warn(`查询功能定价失败（HTTP ${res.status}），跳过前端积分守卫，由后端扣费兜底`)
        return null
      }
      const data = await res.json()
      const info = (data && typeof data === 'object' && data.data) ? data.data : data
      const costNum = Number(info?.cost)
      if (info && typeof info === 'object' && !Number.isNaN(costNum)) {
        return {
          cost: costNum,
          rmbPrice: Number(info?.rmb_price) || 0,
          free: info.free === true,
        }
      }
      return null
    } catch (e) {
      console.warn('查询功能定价异常，跳过前端积分守卫，由后端扣费兜底:', e)
      return null
    }
  }

  /**
   * 功能积分守卫：动态查价 → free/cost=0 放行 → 余额比对，不足弹 PayDialog
   *
   * 铁律：前端禁止硬编码价格；查价失败时放行，由后端扣费兜底。
   *
   * @param feature     - 功能标识字符串（用于查价）
   * @param featureName - 功能名称（用于弹窗展示）
   * @returns true 表示可以继续执行，false 表示已弹窗
   */
  async function checkFeatureCost(feature, featureName) {
    const price = await getFeatureCost(feature)
    // price 为 null（查价失败）或 free/cost=0（免费额度，如 bazi 新用户首3次）时直接放行，不弹充值窗
    if (!price || price.free || price.cost <= 0) return true

    payDialogRmbPrice.value = price.rmbPrice
    return await checkPoints(price.cost, featureName)
  }

  /** 跳转积分中心页面 */
  function goToPoints() {
    showPayDialog.value = false
    router.push('/points')
  }

  /** 跳转会员开通页面（预留） */
  function goToMembership() {
    showPayDialog.value = false
    // 未来: router.push('/membership')
  }

  return {
    showPayDialog,
    payDialogCost,
    payDialogBalance,
    payDialogFeature,
    payDialogRmbPrice,
    checkPoints,
    checkFeatureCost,
    getFeatureCost,
    goToPoints,
    goToMembership,
  }
}
